import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import { motion } from "framer-motion";
import RoleService from "../../services/RoleService";
import { useEffect, useMemo, useState } from "react";
import { IRoleResponseData } from "../../types/Role";
import RoleEditModal from "./RoleEditModal";
import { NavLink } from "react-router-dom";
import SettingsRouter from "./SettingsRouter";
import { openNotification } from "../../utils";

const { Title, Text } = Typography;

const PROTECTED_ROLES = new Set(["ADMIN"]);

export const RoleList = () => {
  const [roles, setRoles] = useState<IRoleResponseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const isAdmin = localStorage.getItem("roleName") === "ADMIN";

  const openCreateModal = () => {
    setSelectedRoleId(null);
    setEditModalOpen(true);
  };

  const openEditModal = (roleId: number) => {
    setSelectedRoleId(roleId);
    setEditModalOpen(true);
  };

  const isProtectedRole = (role: IRoleResponseData) => PROTECTED_ROLES.has(role.name);

  const columns: ColumnsType<IRoleResponseData> = [
    {
      title: "Rol adı",
      dataIndex: "name",
      key: "name",
      width: 220,
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Yetki sayısı",
      key: "permissionCount",
      width: 120,
      align: "center",
      render: (_, row) => row.permissions?.length ?? 0,
    },
    {
      title: "İşlemler",
      key: "actions",
      width: 180,
      align: "center",
      render: (_, row) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(row.id)}
          >
            Düzenle
          </Button>
          {!isProtectedRole(row) ? (
            <Popconfirm
              title="Rol kalıcı olarak silinecek"
              description="Rol, kullanıcı bağlantıları ve yalnızca bu role ait yetki tanımları silinir."
              okText="Sil"
              cancelText="İptal"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteRoles([row.id])}
            >
              <Button type="link" danger icon={<DeleteOutlined />} loading={deleting}>
                Sil
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection: TableRowSelection<IRoleResponseData> = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys as number[]),
      getCheckboxProps: (record) => ({
        disabled: isProtectedRole(record),
      }),
    }),
    [selectedRowKeys]
  );

  const getRoles = () => {
    setLoading(true);
    RoleService.getAll()
      .then((response: any) => {
        setRoles(response.data);
        setSelectedRowKeys([]);
      })
      .catch((e: Error) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  };

  const deleteRoles = async (roleIds: number[]) => {
    if (roleIds.length === 0) return;

    setDeleting(true);
    try {
      if (roleIds.length === 1) {
        await RoleService.remove(roleIds[0]);
      } else {
        await RoleService.bulkDelete(roleIds);
      }
      openNotification(
        "success",
        "Başarılı",
        roleIds.length === 1 ? "Rol silindi" : `${roleIds.length} rol silindi`
      );
      getRoles();
    } catch (error: any) {
      const message = error?.response?.data?.message || "Rol silinemedi";
      openNotification("error", "Hata", message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  const deletableSelectedCount = selectedRowKeys.length;

  return (
    <motion.div
      initial={{ y: 200, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 300, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 40,
      }}
    >
      <div>
        <Row align="middle" justify="space-between">
          <Col>
            <Title className="onemaz-page-title" level={3}>
              {}
            </Title>
            <Breadcrumb
              items={[
                {
                  title: <NavLink to="/">Home</NavLink>,
                },
                {
                  title: "Settings",
                },
                {
                  title: "Roller",
                },
              ]}
            />
          </Col>
          <Col>
            <Space>
              {isAdmin ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                  Yeni Rol
                </Button>
              ) : null}
              {deletableSelectedCount > 0 ? (
                <Popconfirm
                  title={`${deletableSelectedCount} rol kalıcı olarak silinecek`}
                  description="Seçili roller, kullanıcı bağlantıları ve yalnızca o rollere ait yetki tanımları silinir."
                  okText="Seçilenleri Sil"
                  cancelText="İptal"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteRoles(selectedRowKeys)}
                >
                  <Button danger icon={<DeleteOutlined />} loading={deleting}>
                    Seçilenleri Sil ({deletableSelectedCount})
                  </Button>
                </Popconfirm>
              ) : null}
            </Space>
          </Col>
        </Row>
        <SettingsRouter />

        <Card style={{ marginTop: 24 }}>
          <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
            <Text type="secondary">
              Rol silindiğinde kullanıcı atamaları kaldırılır. Başka role bağlı olmayan yetki
              tanımları da silinir. ADMIN rolü korunur.
            </Text>
          </Space>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={roles}
            rowSelection={rowSelection}
          />
        </Card>
      </div>

      <RoleEditModal
        open={editModalOpen}
        roleId={selectedRoleId}
        onClose={() => setEditModalOpen(false)}
        onSuccess={getRoles}
      />
    </motion.div>
  );
};
